import React, { useContext, useMemo, useState, useEffect } from 'react';
import { useTestConfiguratorContext } from '../test-configurator-provider';
import {
  TestConfigStep,
  TestConfigStepsContextValue,
  TestConfigStepType,
  TestConfiguratorStepsData,
} from './test-config-steps-provider.interfaces';
import { getNextStepType, getNextUncompletedStepIndex, getRelatedSteps } from './functions';
import { getConfigStepsDataToMatchCompletedSteps } from './functions/get-config-steps-data-to-match-completed-steps';

const TestConfigStepsContext = React.createContext<TestConfigStepsContextValue>({} as any);
const INITIAL_STEPS = [{ stepType: TestConfigStepType.SET_OPPOSITION, isCompleted: false }];

export const TestConfigStepsProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const [configStepsData, setConfigStepsData] = useState<TestConfiguratorStepsData>({
    currentStepIndex: 0,
    steps: INITIAL_STEPS,
    isConfigCompleted: false,
  });
  const { configData } = useTestConfiguratorContext();
  const [shouldUpdateSteps, setShouldUpdateSteps] = useState(false);
  const [shouldMatchCompletedStepsWithConfigData, setShouldMatchCompletedStepsWithConfigData] = useState(false);
  const [isCompletedStepChanged, setIsCompletedStepChanged] = useState(false);

  const getUpdatedStepsData = (currentConfigStepsData: TestConfiguratorStepsData): TestConfiguratorStepsData => {
    const { steps, currentStepIndex } = currentConfigStepsData;

    const currentStep = steps[currentStepIndex];
    const nextStepType = !!currentStep && getNextStepType({ currentStepType: currentStep.stepType, configData });
    const currentNextStep = steps[currentStepIndex + 1];
    const isLastKnownStep = currentStepIndex + 1 == steps.length;

    // Remains at the same step and set isConfigCompleted to true
    if (!nextStepType || nextStepType === TestConfigStepType.FINISH) {
      return {
        ...configStepsData,
        steps: steps.slice(0, currentStepIndex + 1),
        currentStepIndex: currentStepIndex + 1,
        isConfigCompleted: true,
      };
    }

    if (isLastKnownStep) {
      // Add next step to steps and move index to next step
      return {
        ...configStepsData,
        steps: [...steps, { stepType: nextStepType, isCompleted: false }],
        currentStepIndex: currentStepIndex + 1,
        isConfigCompleted: false,
      };
    }

    if (currentNextStep?.stepType === nextStepType && !isCompletedStepChanged) {
      const stepsWithUpdatedRelatedSteps = isCompletedStepChanged
        ? getUpdateRelatedStepsStatusOnChange(steps, currentStepIndex)
        : steps;

      let nextStepIndex = getNextUncompletedStepIndex(currentStepIndex, stepsWithUpdatedRelatedSteps);
      return {
        ...configStepsData,
        steps: stepsWithUpdatedRelatedSteps,
        currentStepIndex: nextStepIndex,
        isConfigCompleted: nextStepIndex >= steps.length,
      };
    }

    return {
      ...configStepsData,
      steps: [...steps.slice(0, currentStepIndex + 1), { stepType: nextStepType, isCompleted: false }],
      currentStepIndex: currentStepIndex + 1,
      isConfigCompleted: false,
    };
  };

  useEffect(() => {
    if (shouldUpdateSteps) {
      const updatedStepsData = getUpdatedStepsData(configStepsData);
      setConfigStepsData(updatedStepsData);
      setShouldUpdateSteps(false);
    }
  }, [configData, shouldUpdateSteps]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (shouldMatchCompletedStepsWithConfigData) {
      const matchingConfigStepsData = getConfigStepsDataToMatchCompletedSteps(configData);
      setConfigStepsData({ ...configStepsData, ...matchingConfigStepsData });
      setShouldUpdateSteps(true);
    }
    setShouldMatchCompletedStepsWithConfigData(false);
  }, [shouldMatchCompletedStepsWithConfigData]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Set status to not completed for steps related to current step.
   */
  const getUpdateRelatedStepsStatusOnChange = (steps: TestConfigStep[], changedStepIndex: number) => {
    const currentStep = steps[changedStepIndex];
    const relatedSteps = getRelatedSteps(currentStep.stepType);

    if (relatedSteps) {
      return steps.map(step => {
        return !relatedSteps.includes(step.stepType) ? step : { ...step, isCompleted: false };
      });
    }
    return steps;
  };

  const completeStep = (stepIndex: number, { isChanged }: { isChanged?: boolean } = {}) => {
    const { steps } = configStepsData;

    if (stepIndex < 0) {
      return;
    }
    if (stepIndex >= steps.length) {
      return;
    }

    steps[stepIndex].isCompleted = true;
    setIsCompletedStepChanged(!!isChanged);
    setShouldUpdateSteps(true);
  };

  const completeCurrentStep = ({ isChanged }: { isChanged: boolean }) => {
    const { currentStepIndex } = configStepsData;
    completeStep(currentStepIndex, { isChanged });
  };

  const goToStep = (stepIndex: number) => {
    const updatedSteps = [...configStepsData.steps];
    updatedSteps[stepIndex].isCompleted = false;

    setConfigStepsData({
      ...configStepsData,
      steps: updatedSteps,
      currentStepIndex: stepIndex,
      isConfigCompleted: false,
    });
  };

  const getStepByType = (stepType: TestConfigStepType) => {
    const { currentStepIndex, steps } = configStepsData;
    const stepIndex = steps.findIndex(step => step.stepType === stepType);
    return {
      stepIndex: stepIndex,
      stepData: steps[stepIndex],
      isCurrentStep: stepIndex === currentStepIndex,
    };
  };

  const matchCompletedStepsWithConfigData = () => {
    setShouldMatchCompletedStepsWithConfigData(true);
  };

  const value: TestConfigStepsContextValue = useMemo(() => {
    return {
      configStepsData,
      completeCurrentStep,
      goToStep,
      getStepByType,
      matchCompletedStepsWithConfigData,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configStepsData]);

  return <TestConfigStepsContext.Provider value={value}>{children}</TestConfigStepsContext.Provider>;
};

export const useTestConfigStepsContext = () => {
  const testConfigStepsContext = useContext(TestConfigStepsContext);
  if (!testConfigStepsContext) {
    console.warn('useTestConfiguratorContextValue must be used inside TestConfiguratorContext provider.');
  }
  return testConfigStepsContext;
};
